import React from "react";
import { format } from "date-fns";

import type { BoxProps } from "@/ui/atoms/Box";
import Box from "@/ui/atoms/Box";

import type { TDateRegion, TDatePart } from "./types";
import { getDateFnsFormat } from "./getDateFnsFormat";

export const LoadingDots: React.FC = () => (
    <Box
        sx={{
            "@keyframes dotFade": {
                "0%, 100%": { opacity: 0.2 },
                "50%": { opacity: 1 },
            },
            display: "inline-flex",
            "& > span": {
                animation: "dotFade 1.4s infinite",
            },
            "& > span:nth-of-type(2)": {
                animationDelay: "0.2s",
            },
            "& > span:nth-of-type(3)": {
                animationDelay: "0.4s",
            },
        }}
    >
        <Box component="span">.</Box>
        <Box component="span">.</Box>
        <Box component="span">.</Box>
    </Box>
);

export type ViewFormattedDateParams = {
    useState: () => {
        userRegion: TDateRegion | null;
    };
};

export type ViewFormattedDateProps = BoxProps & {
    date: string | Date | number;
    parts?: TDatePart[];
};

export const testId = "ViewFormattedDate";

export const createViewFormattedDate = ({
    useState,
}: ViewFormattedDateParams) => {
    const ViewFormattedDate: React.FC<ViewFormattedDateProps> = props => {
        const {
            date,
            parts = ["day", "month", "year"],
            sx,
            ...otherProps
        } = props;

        const { userRegion } = useState();

        const formatString = React.useMemo(
            () => (userRegion ? getDateFnsFormat(parts, userRegion) : ""),
            [parts, userRegion],
        );

        const formattedDate = React.useMemo(() => {
            try {
                const dateObj =
                    typeof date === "string" ? new Date(date) : date;
                return format(dateObj, formatString);
            } catch (e) {
                console.error("[ViewFormattedDate]Error formatting date:", e);
                return String(date);
            }
        }, [date, formatString]);

        if (userRegion === null) {
            return (
                <Box
                    data-testid={testId}
                    sx={{
                        display: "inline",
                        ...sx,
                    }}
                    {...otherProps}
                >
                    <LoadingDots />
                </Box>
            );
        }

        return (
            <Box
                data-testid={testId}
                sx={{
                    display: "inline",
                    ...sx,
                }}
                {...otherProps}
            >
                {formattedDate}
            </Box>
        );
    };

    return ViewFormattedDate;
};
